/**
 * One table's round trip: out as a file, edited, back in.
 *
 * Importing is not "read a file and write rows". Between the file and the
 * table sits a correspondence that is not known yet, and settling it is the
 * whole of the difficulty: which column is which field, which row is which
 * record, and what to do when two rows claim to be the same one. So an import
 * is a negotiation that converges on a Plan, and then the Plan is carried out.
 *
 * Every type here mirrors a DTO in plan/import-plan-contract-zh.md field for
 * field. Where the contract types a value as a string drawn from a fixed list,
 * this narrows it to a union - the same shape, held more tightly, so a typo in
 * a comparison is a compile error rather than a branch that never runs.
 */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/** The tables that can make this trip. Adding one is adding a schema. */
export type TransferTable = 'customers' | 'leads' | 'rolloff-hires';

// ── The field table: the only thing that differs between entities ──────────

export interface TransferField {
  /** Our field name. The same string MappingEntry.field carries. */
  name: string;
  label: string;
  /** Empty makes the row RequiredMissing. */
  required: boolean;
  /** False = exported but not importable; an edit to it becomes IgnoredEdit. */
  writable: boolean;
  /**
   * Not imported by default, but honoured when somebody maps a column to it
   * BY HAND (ruled 2026-08-30). False on the identity column, which no choice
   * reopens. Absent from a backend that predates the ruling.
   */
  overridable?: boolean;
  /** Of those, the ones the system works out - so an override is kept and
   *  then replaced at the next recalculation. */
  recalculated?: boolean;
  /**
   * What a record gets when no column feeds this field and nobody says
   * otherwise. Absent means it arrives empty, which is true of nearly every
   * field: the import invents nothing.
   */
  defaultValue?: string | null;
}

export interface TransferSchema {
  table: TransferTable;
  fields: TransferField[];
  /**
   * The file extensions this table takes, dots and all.
   *
   * The server refuses anything else before it opens the stream, so the
   * picker has to offer exactly this. It used to carry its own list and the
   * two disagreed in both directions: it offered .tsv and .txt, which the
   * server rejects, and hid .xlsx, which the server accepts.
   */
  acceptedExtensions: string[];
}

// ── How the columns were matched up, and on what evidence ─────────────────

/**
 * Not decoration. A reader has to be able to tell what the machine knew from
 * what it guessed, because only the guesses need checking.
 */
export type MappingHow =
  | 'exact'
  | 'fuzzy'
  | 'remembered'
  | 'chosen'
  | 'unmatched';

export interface MappingEntry {
  field: string;
  /** The file's column. Null = this field has no source in the file. */
  header: string | null;
  how: MappingHow;
}

/**
 * A column the file has that we recognise and will not write.
 *
 * Distinct from an entry with no field, which is a column we do not recognise
 * at all - telling somebody that one "cannot be imported" would be a lie, and
 * they would go looking for the rule that forbids it.
 */
export interface UnwritableColumn {
  /** What the file calls it. */
  header: string;
  /** What it is on our side. */
  field: string;
  /**
   * Its value is worked out by us, rather than merely having nowhere to go.
   * The two need different words: "change it where it is calculated" is
   * useless advice for a column that is simply not writable, and sends the
   * reader hunting for a screen that does not exist.
   */
  computed: boolean;
  /**
   * How many rows carried a value there. One is somebody typing; a hundred is
   * their own export coming home. Optional: a backend that predates it says
   * nothing rather than zero.
   */
  rows?: number;
}

export interface Mapping {
  entries: MappingEntry[];
  /** Columns we recognise and do not write. Absent = none. */
  unwritable?: UnwritableColumn[];
  /** Whether this mapping was remembered or inferred fresh. */
  remembered: boolean;
  /**
   * The columns the file actually has, in the order it has them. Entries
   * cannot answer this: a file column that matched no field of ours never
   * appears there, and those are exactly the ones an unmatched field needs to
   * be able to pick from. Read by the backend, not parsed again here - two
   * readers of one file are two chances to disagree about it.
   */
  headers: string[];
}

/**
 * A mapping a person has already settled, handed back in to re-stage.
 *
 * There was a `SavedMapping` beside this until 2026-09-02 - a mapping kept on
 * the account under a name - with a service call to write one and another to
 * list them. A mapping is kept as a FILE now, so both are gone from here and
 * the shape of one lives in `mapping-file.ts`, which is the only thing that
 * reads or writes it.
 */
export interface MappingChoice {
  /** Field to header. */
  fields: Record<string, string>;
  /**
   * Field to a value put on EVERY row, for a field no column feeds. "These
   * four hundred leads are all in Florida" is a thing the person knows and
   * the file does not say.
   */
  constants?: Record<string, string>;
}

// ── The Plan: one verdict per row, with its reason ────────────────────────

export type PlanAction = 'Create' | 'Update' | 'Skip' | 'Blocked';

export interface PlanRow {
  /** The line in the file, from 1. */
  rowNo: number;
  action: PlanAction;
  /** Which record an Update points at; null otherwise. */
  targetId: number | null;
  /** The stable code for a Skip or a Blocked. Prose belongs in detail. */
  reason: string | null;
  /**
   * Everything else this row needs to say. Reason answers "where does this row
   * go"; a row can still deserve a caution while going in - a renamed row is
   * written beside the record it came from, and that is a Create with nothing
   * for Reason to hold. A list, because one row can be two things at once.
   */
  notes: string[] | null;
  detail: string | null;
  /** Normalized field values, for the preview. */
  values: Record<string, string | null>;
}

export interface PlanSummary {
  total: number;
  create: number;
  update: number;
  skip: number;
  blocked: number;
}

export interface TransferPlan {
  planId: string;
  fileName: string;
  mapping: Mapping;
  rows: PlanRow[];
  summary: PlanSummary;
}

// ── What the person decided, and what came of carrying it out ─────────────

export interface PlanDecisions {
  /** Field to header, overriding what was inferred. */
  mapping: Record<string, string> | null;
  /** Row number to action, overriding the default verdict. */
  rowActions: Record<number, PlanAction> | null;
  rememberMapping: boolean;
  /**
   * One answer for a whole class of rows, the ones nobody scrolled to
   * included. Two hundred repeats is two hundred row actions, which in
   * practice means the first few are answered and the rest default without
   * anyone deciding. rowActions wins over this, so "all of them like that,
   * except this one" is sayable.
   */
  reasonActions: Record<string, PlanAction> | null;
}

export interface TableApplyResult {
  planId: string;
  operation: 'Apply' | 'Undo';
  created: number;
  updated: number;
  skipped: number;
  blocked: number;
  /** On an undo, the rows that had changed since and were left alone. */
  failed: number;
  canUndo: boolean;
  /** The rows that did not go in, each carrying why. */
  notImported: PlanRow[];
}

@Injectable({ providedIn: 'root' })
export class TableImportExportService {
  constructor(private readonly http: HttpClient) {}

  /**
   * The field table for one entity. Everything the dialog does differently for
   * Customers than for Leads has to arrive through here - that is what lets
   * one dialog serve both without ever asking which table it is looking at.
   */
  getSchema(table: TransferTable): Observable<TransferSchema> {
    return this.http.get<TransferSchema>(`/api/${table}/transfer/schema`);
  }

  /**
   * Read the file and work out what it would do. Writes nothing: the Plan is
   * the preview, so there is no separate preview to keep in step with it.
   */
  stage(
    table: TransferTable,
    file: File,
    mapping?: MappingChoice,
  ): Observable<TransferPlan> {
    const body = new FormData();
    body.append('file', file, file.name);
    if (mapping) body.append('mapping', JSON.stringify(mapping));
    return this.http.post<TransferPlan>(`/api/${table}/transfer/stage`, body);
  }

  /** Carry out the Plan, as amended, and write a ledger that can undo it. */
  apply(
    table: TransferTable,
    planId: string,
    decisions: PlanDecisions,
  ): Observable<TableApplyResult> {
    return this.http.post<TableApplyResult>(
      `/api/${table}/transfer/${planId}/apply`, decisions);
  }

  /** Walk the ledger backwards. */
  /**
   * Start the apply as a job and get its id back.
   *
   * Used instead of `apply` for anything but the smallest file: nginx closes
   * a proxied request after sixty seconds, and the apply used to carry on
   * writing behind a browser that had been told it failed.
   */
  applyJob(
    table: TransferTable, planId: string, decisions: PlanDecisions,
  ): Observable<{ jobId: string; alreadyRunning: boolean }> {
    return this.http.post<{ jobId: string; alreadyRunning: boolean }>(
      `/api/${table}/transfer/${planId}/apply-job`, decisions);
  }

  undo(table: TransferTable, planId: string): Observable<TableApplyResult> {
    return this.http.post<TableApplyResult>(
      `/api/${table}/transfer/${planId}/undo`, {});
  }
}
